/**
 * TaskProgressDashboard.tsx
 * 
 * Dashboard per visualizzare lo stato di avanzamento dei task per gli admin.
 * Mostra statistiche, grafici di completamento, distribuzione per status e workload utenti.
 */

import React from 'react';
import {
  Card,
  Grid,
  Title,
  Text,
  Progress,
  Group,
  Stack,
  Badge,
  RingProgress,
  Table,
  Alert,
  SimpleGrid,
  ThemeIcon,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconChecks,
  IconClock,
  IconPlayerPlay,
  IconAlertTriangle,
  IconUsers,
  IconTrendingUp,
  IconCalendarTime,
  IconChartPie,
} from '@tabler/icons-react';

interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  overdue: number;
  completionRate: number;
}

interface UserWorkload {
  userId: number;
  userName: string;
  userEmail: string;
  totalTasks: number;
  activeTasks: number;
  overdueTasks: number;
  completionRate: number;
}

interface TaskProgressDashboardProps {
  requestId?: string;
  stats?: TaskStats;
  userWorkloads?: UserWorkload[];
  isLoading?: boolean;
  className?: string;
}

const STATUS_COLORS = {
  todo: 'blue',
  inProgress: 'yellow',
  done: 'green',
  overdue: 'red',
} as const;

export function TaskProgressDashboard({
  requestId,
  stats,
  userWorkloads = [],
  isLoading = false,
  className,
}: TaskProgressDashboardProps) {
  
  if (isLoading) {
    return (
      <Card className={className}>
        <Center h={200}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>Caricamento dashboard...</Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card className={className}>
        <Alert
          icon={<IconChartPie size="1rem" />}
          title="Dati non disponibili"
          color="gray"
        >
          Non sono disponibili statistiche per i task.
        </Alert>
      </Card>
    );
  }

  // Calcola dati per ring progress
  const ringData = [
    { label: 'Completati', value: stats.done, color: STATUS_COLORS.done },
    { label: 'In Corso', value: stats.inProgress, color: STATUS_COLORS.inProgress },
    { label: 'Da Fare', value: stats.todo, color: STATUS_COLORS.todo },
  ].filter(item => item.value > 0);

  return (
    <div className={className}>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>Task Progress Dashboard</Title>
            <Text size="sm" c="dimmed">
              {requestId ? `Richiesta #${requestId}` : 'Panoramica generale'}
            </Text>
          </div>
          <Badge size="lg" variant="light">
            {stats.total} Task Totali
          </Badge>
        </Group>

        {/* Overview Cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Completamento
                </Text>
                <Text fw={700} size="xl">
                  {stats.completionRate.toFixed(1)}%
                </Text>
              </div>
              <ThemeIcon color="green" variant="light" size={38} radius="md">
                <IconTrendingUp size="1.8rem" />
              </ThemeIcon>
            </Group>
            <Progress
              value={stats.completionRate}
              mt="md"
              size="sm"
              radius="xl"
              color="green"
            />
          </Card>

          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Completati
                </Text>
                <Text fw={700} size="xl">
                  {stats.done}
                </Text>
              </div>
              <ThemeIcon color="green" variant="light" size={38} radius="md">
                <IconChecks size="1.8rem" />
              </ThemeIcon>
            </Group>
          </Card>

          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  In Corso
                </Text>
                <Text fw={700} size="xl">
                  {stats.inProgress}
                </Text>
              </div>
              <ThemeIcon color="yellow" variant="light" size={38} radius="md">
                <IconPlayerPlay size="1.8rem" />
              </ThemeIcon>
            </Group>
          </Card>

          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  In Ritardo
                </Text>
                <Text fw={700} size="xl">
                  {stats.overdue}
                </Text>
              </div>
              <ThemeIcon color="red" variant="light" size={38} radius="md">
                <IconAlertTriangle size="1.8rem" />
              </ThemeIcon>
            </Group>
          </Card>
        </SimpleGrid>

        <Grid>
          {/* Distribuzione Task */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md" withBorder h="100%">
              <Title order={4} mb="md">
                <Group gap="xs">
                  <IconChartPie size="1.2rem" />
                  Distribuzione Task
                </Group>
              </Title>
              
              {stats.total > 0 ? (
                <Group justify="center" align="flex-start" gap="xl">
                  <RingProgress
                    size={180}
                    thickness={16}
                    sections={ringData.map(item => ({
                      value: (item.value / stats.total) * 100,
                      color: item.color,
                    }))}
                    label={
                      <div style={{ textAlign: 'center' }}>
                        <Text fw={700} size="xl">
                          {stats.total}
                        </Text>
                        <Text size="sm" c="dimmed">
                          Totali
                        </Text>
                      </div>
                    }
                  />
                  
                  <Stack gap="sm" justify="center">
                    {ringData.map((item) => (
                      <Group key={item.label} gap="xs">
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: `var(--mantine-color-${item.color}-filled)`,
                          }}
                        />
                        <Text size="sm">
                          {item.label}: <strong>{item.value}</strong>
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Group>
              ) : (
                <Center h={200}>
                  <Text c="dimmed">Nessun task disponibile</Text>
                </Center>
              )}
            </Card>
          </Grid.Col>

          {/* Workload Utenti */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card padding="lg" radius="md" withBorder h="100%">
              <Title order={4} mb="md">
                <Group gap="xs">
                  <IconUsers size="1.2rem" />
                  Carico di Lavoro Utenti
                </Group>
              </Title>
              
              {userWorkloads.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Utente</Table.Th>
                      <Table.Th style={{ textAlign: 'center' }}>Attivi</Table.Th>
                      <Table.Th style={{ textAlign: 'center' }}>Totali</Table.Th>
                      <Table.Th style={{ textAlign: 'center' }}>%</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {userWorkloads.slice(0, 5).map((user) => (
                      <Table.Tr key={user.userId}>
                        <Table.Td>
                          <div>
                            <Text fw={500} size="sm">
                              {user.userName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {user.userEmail}
                            </Text>
                          </div>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Group gap="xs" justify="center">
                            <Text fw={500}>{user.activeTasks}</Text>
                            {user.overdueTasks > 0 && (
                              <Badge color="red" size="xs" style={{ color: 'white' }}>
                                {user.overdueTasks}
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text>{user.totalTasks}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text fw={500} c={user.completionRate >= 80 ? 'green' : user.completionRate >= 50 ? 'yellow' : 'red'}>
                            {user.completionRate.toFixed(0)}%
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Center h={150}>
                  <Stack align="center" gap="xs">
                    <IconUsers size="2rem" color="var(--mantine-color-gray-5)" />
                    <Text c="dimmed" size="sm">Nessun dato utente disponibile</Text>
                  </Stack>
                </Center>
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* Alert per task in ritardo */}
        {stats.overdue > 0 && (
          <Alert
            icon={<IconCalendarTime size="1rem" />}
            title="Attenzione: Task in ritardo"
            color="red"
            variant="light"
          >
            <Text size="sm">
              Ci sono <strong>{stats.overdue} task in ritardo</strong> che richiedono attenzione immediata.
              Controlla le scadenze e riassegna le priorità se necessario.
            </Text>
          </Alert>
        )}
      </Stack>
    </div>
  );
}

export default TaskProgressDashboard;